import { useState, useRef, useCallback } from "react"
import { Link } from "react-router-dom"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import {
  MessageSquarePlus, Upload, X, Loader2,
  Bug, Lightbulb, MessageCircle,
  ChevronDown, ChevronRight, ExternalLink,
} from "lucide-react"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { useToast } from "@/hooks/use-toast"
import { useAuth } from "@/hooks/useAuth"
import { apiClient } from "@/lib/api"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { formatDistanceToNow } from "date-fns"

type FeedbackType = "bug" | "feature" | "general"
type Tab = "new" | "history"

interface AttachmentPreview {
  file: File
  preview: string
}

interface FeedbackComment {
  id: number | string
  body: string
  created_at: string
  user_name: string | null
  user_avatar: string | null
}

/**
 * One of the user's own reports, as GET /feedback/mine returns it: the FOR
 * board task it was filed as. `url` is null when the user cannot open the FOR
 * board, and `title`/`state` are null if the task was deleted.
 */
interface MyIssueItem {
  id: number
  task_id: number | null
  key: string | null
  identifier: string | null
  title: string | null
  description: string | null
  url: string | null
  state: string | null
  status: string | null
  labels?: Array<{ name: string; color: string | null }>
  comments?: FeedbackComment[]
  created_at: string
}

// Task state (docs/boards-api.md §3) → Tailwind color classes
const stateColors: Record<string, string> = {
  triage: "bg-orange-100 text-orange-700 dark:bg-orange-900 dark:text-orange-300",
  backlog: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
  todo: "bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300",
  in_progress: "bg-yellow-100 text-yellow-700 dark:bg-yellow-900 dark:text-yellow-300",
  done: "bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300",
  canceled: "bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300",
}

// Maps the first label name to an icon component for issue list display
const labelIconMap: Record<string, typeof Bug> = {
  Bug: Bug,
  Feature: Lightbulb,
}

const typeOptions: { value: FeedbackType; label: string; icon: React.ReactNode; color: string }[] = [
  { value: "bug", label: "Bug", icon: <Bug className="h-3.5 w-3.5" />, color: "border-red-500 bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-400" },
  { value: "feature", label: "Feature", icon: <Lightbulb className="h-3.5 w-3.5" />, color: "border-purple-500 bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-400" },
  { value: "general", label: "General", icon: <MessageCircle className="h-3.5 w-3.5" />, color: "border-gray-500 bg-gray-50 text-gray-700 dark:bg-gray-950 dark:text-gray-400" },
]

export function FeedbackModal() {
  const [open, setOpen] = useState(false)
  const [tab, setTab] = useState<Tab>("new")
  const [type, setType] = useState<FeedbackType>("general")
  const [title, setTitle] = useState("")
  const [description, setDescription] = useState("")
  // Mirrors FeedbackController's `description => max:10000`. Keep the two in step —
  // the client cap is a courtesy, the server rule is the contract.
  const DESCRIPTION_MAX = 10000
  const nearDescriptionLimit = description.length > DESCRIPTION_MAX * 0.9
  const atDescriptionLimit = description.length >= DESCRIPTION_MAX
  const [attachments, setAttachments] = useState<AttachmentPreview[]>([])
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { toast } = useToast()
  const { currentLicense } = useAuth()
  const queryClient = useQueryClient()

  const resetForm = useCallback(() => {
    setType("general")
    setTitle("")
    setDescription("")
    setAttachments([])
  }, [])

  // Fetch the user's own reports (FOR board tasks) when the history tab is shown
  const { data: myIssues, isLoading: historyLoading } = useQuery({
    queryKey: ["my-issues"],
    queryFn: () => apiClient.get<{ data: MyIssueItem[] }>("/feedback/mine"),
    enabled: open && tab === "history",
  })

  const mutation = useMutation({
    mutationFn: async (formData: FormData) => {
      return apiClient.post<{ message: string; issue: { identifier: string; url: string } }>("/feedback", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      })
    },
    onSuccess: (response) => {
      const identifier = response?.issue?.identifier
      toast({
        title: "Feedback submitted!",
        description: identifier
          ? `Issue ${identifier} created. Thanks for helping us improve Forge.`
          : "Thanks for helping us improve Forge.",
      })
      resetForm()
      queryClient.invalidateQueries({ queryKey: ["my-issues"] })
      setTab("history")
    },
    onError: () => {
      toast({ title: "Failed to submit feedback", description: "Please try again.", variant: "destructive" })
    },
  })

  const handleSubmit = () => {
    if (!title.trim() || !description.trim()) return

    const formData = new FormData()
    formData.append("type", type)
    formData.append("title", title.trim())
    formData.append("description", description.trim())
    formData.append("page_url", window.location.href)
    formData.append("user_agent", navigator.userAgent)
    formData.append("screen_resolution", `${window.screen.width}x${window.screen.height}`)

    if (currentLicense?.site_id) {
      formData.append("site_id", String(currentLicense.site_id))
    }

    formData.append("metadata", JSON.stringify({
      site_name: currentLicense?.domain || null,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      timestamp: new Date().toISOString(),
    }))

    attachments.forEach((att) => {
      formData.append("attachments[]", att.file)
    })

    mutation.mutate(formData)
  }

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files) return
    const remaining = 3 - attachments.length
    Array.from(files).slice(0, remaining).forEach((file) => {
      const reader = new FileReader()
      reader.onload = (ev) => {
        setAttachments((prev) => [...prev, { file, preview: ev.target?.result as string }])
      }
      reader.readAsDataURL(file)
    })
    e.target.value = ""
  }

  const removeAttachment = (index: number) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index))
  }

  /** Get the icon for an issue based on its first label, fallback to MessageCircle */
  const getIssueIcon = (labels: Array<{ name: string; color: string | null }>) => {
    const firstLabel = labels[0]?.name
    return labelIconMap[firstLabel] || MessageCircle
  }

  /** Strip the metadata footer (everything after the first ---) from issue descriptions */
  const cleanDescription = (desc: string | null) => {
    if (!desc) return ""
    const parts = desc.split("\n---\n")
    return parts[0].trim()
  }

  const issueItems = myIssues?.data ?? []

  return (
    <Popover open={open} onOpenChange={(v) => { setOpen(v); if (!v) { resetForm(); setTab("new"); setExpandedId(null) } }}>
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-muted-foreground hover:text-foreground"
              >
                <MessageSquarePlus className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
          </TooltipTrigger>
          <TooltipContent>Send Feedback</TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <PopoverContent className="w-[400px] p-0" align="end">
        {/* Tab bar */}
        <div className="flex border-b">
          <button
            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
              tab === "new"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setTab("new")}
          >
            New Feedback
          </button>
          <button
            className={`flex-1 px-3 py-2 text-xs font-medium transition-colors ${
              tab === "history"
                ? "border-b-2 border-primary text-primary"
                : "text-muted-foreground hover:text-foreground"
            }`}
            onClick={() => setTab("history")}
          >
            My Issues
          </button>
        </div>

        <div className="p-3">
          {tab === "new" ? (
            /* -- New Feedback Form -- */
            <div className="space-y-3">
              <div className="flex gap-1.5">
                {typeOptions.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => setType(opt.value)}
                    className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md border text-xs font-medium transition-colors ${
                      type === opt.value ? opt.color : "border-border bg-background text-muted-foreground hover:bg-muted"
                    }`}
                  >
                    {opt.icon}
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="feedback-title" className="text-xs">Title</Label>
                <Input
                  id="feedback-title"
                  placeholder="Brief summary..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={255}
                  className="h-8 text-sm"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="feedback-description" className="text-xs">Description</Label>
                <Textarea
                  id="feedback-description"
                  placeholder={type === "bug"
                    ? "What happened? What did you expect instead?\n\nInclude: steps to reproduce, browser, and any error messages so we can replicate the issue."
                    : "Describe the issue or idea in detail..."
                  }
                  value={description}
                  onChange={(e) => setDescription(e.target.value.slice(0, DESCRIPTION_MAX))}
                  rows={8}
                  className="text-sm resize-y min-h-[8rem]"
                />
                {/* A silent `maxLength` truncates a long paste with no warning, which is
                    how someone loses half a submission and only discovers it afterwards.
                    Show the count always; say plainly what happens at the limit. */}
                <div className="flex justify-between gap-2 text-[10px]">
                  <span className={atDescriptionLimit ? "text-destructive font-medium" : "text-amber-600 dark:text-amber-500"}>
                    {atDescriptionLimit
                      ? "Limit reached \u2014 anything further will not be saved. Send the rest as a second submission."
                      : nearDescriptionLimit
                        ? `${(DESCRIPTION_MAX - description.length).toLocaleString()} characters left`
                        : ""}
                  </span>
                  <span className={`tabular-nums ${atDescriptionLimit ? "text-destructive font-medium" : "text-muted-foreground"}`}>
                    {description.length.toLocaleString()} / {DESCRIPTION_MAX.toLocaleString()}
                  </span>
                </div>
                {type === "bug" && !description && (
                  <p className="text-[10px] text-muted-foreground">
                    Tip: Include steps to reproduce, your browser, and screenshots so we can find and fix the issue faster.
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Attachments ({attachments.length}/3)</Label>
                <div className="flex gap-1.5">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={attachments.length >= 3}
                  >
                    <Upload className="h-3.5 w-3.5 mr-1" />
                    Upload
                  </Button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/gif,image/webp"
                    multiple
                    className="hidden"
                    onChange={handleFileUpload}
                  />
                </div>

                {attachments.length > 0 && (
                  <div className="flex gap-2 mt-1.5">
                    {attachments.map((att, i) => (
                      <div key={i} className="relative group">
                        <img
                          src={att.preview}
                          alt={`Attachment ${i + 1}`}
                          className="h-16 w-16 object-cover rounded border"
                        />
                        <button
                          type="button"
                          onClick={() => removeAttachment(i)}
                          className="absolute -top-1 -right-1 h-4 w-4 bg-destructive text-destructive-foreground rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        >
                          <X className="h-2.5 w-2.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end pt-1">
                <Button
                  size="sm"
                  className="h-7 text-xs"
                  onClick={handleSubmit}
                  disabled={!title.trim() || !description.trim() || mutation.isPending}
                >
                  {mutation.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" />}
                  Submit
                </Button>
              </div>
            </div>
          ) : (
            /* -- My Issues List (FOR board tasks) -- */
            <div className="space-y-1">
              {historyLoading ? (
                <div className="flex items-center justify-center py-8 text-muted-foreground text-xs">
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Loading...
                </div>
              ) : issueItems.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground text-xs">
                  No issues submitted yet
                </div>
              ) : (
                <div className="max-h-[360px] overflow-y-auto -mx-1 px-1 space-y-0.5">
                  {issueItems.map((item) => {
                    const labels = item.labels ?? []
                    const comments = item.comments ?? []
                    const Icon = getIssueIcon(labels)
                    const isExpanded = expandedId === item.id
                    const colorClass = stateColors[item.state ?? ""] || stateColors.backlog
                    const taskKey = item.key ?? item.identifier

                    return (
                      <div key={item.id} className="rounded-md border border-transparent hover:border-border transition-colors">
                        {/* Issue summary row */}
                        <button
                          type="button"
                          className="w-full flex items-start gap-2 p-2 text-left"
                          onClick={() => setExpandedId(isExpanded ? null : item.id)}
                        >
                          {isExpanded
                            ? <ChevronDown className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                            : <ChevronRight className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                          }
                          <Icon className="h-3.5 w-3.5 mt-0.5 shrink-0 text-muted-foreground" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              {taskKey && (
                                <Badge variant="outline" className="h-4 px-1 text-[10px] shrink-0 font-mono">
                                  {taskKey}
                                </Badge>
                              )}
                              <span className="text-xs font-medium truncate">{item.title ?? "Removed"}</span>
                            </div>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              {item.status && (
                                <span className={`inline-flex items-center px-1.5 py-0 rounded text-[10px] font-medium ${colorClass}`}>
                                  {item.status}
                                </span>
                              )}
                              <span className="text-[10px] text-muted-foreground/70">
                                {formatDistanceToNow(new Date(item.created_at), { addSuffix: true })}
                              </span>
                              {comments.length > 0 && (
                                <span className="flex items-center gap-0.5 text-[10px] text-muted-foreground/70">
                                  <MessageCircle className="h-2.5 w-2.5" />
                                  {comments.length}
                                </span>
                              )}
                            </div>
                          </div>
                        </button>

                        {/* Expanded detail: comments thread + Open in Forge link */}
                        {isExpanded && (
                          <div className="px-2 pb-2 ml-7 space-y-2">
                            {/* Description (stripped of internal metadata) */}
                            <p className="text-[11px] text-muted-foreground whitespace-pre-line line-clamp-4">
                              {cleanDescription(item.description)}
                            </p>

                            {/* Labels */}
                            {labels.length > 0 && (
                              <div className="flex gap-1 flex-wrap">
                                {labels.map((label) => (
                                  <span
                                    key={label.name}
                                    className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium bg-muted"
                                  >
                                    <span
                                      className="h-1.5 w-1.5 rounded-full shrink-0"
                                      style={{ backgroundColor: label.color ?? undefined }}
                                    />
                                    {label.name}
                                  </span>
                                ))}
                              </div>
                            )}

                            {/* Comments thread */}
                            {comments.length > 0 && (
                              <div className="space-y-1.5 border-t pt-2">
                                <span className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                                  Comments
                                </span>
                                {comments.map((comment) => (
                                  <div key={comment.id} className="flex gap-1.5">
                                    {comment.user_avatar ? (
                                      <img
                                        src={comment.user_avatar}
                                        alt=""
                                        className="h-4 w-4 rounded-full shrink-0 mt-0.5"
                                      />
                                    ) : (
                                      <div className="h-4 w-4 rounded-full shrink-0 mt-0.5 bg-muted flex items-center justify-center">
                                        <span className="text-[8px] font-medium text-muted-foreground">
                                          {(comment.user_name || "?")[0].toUpperCase()}
                                        </span>
                                      </div>
                                    )}
                                    <div className="flex-1 min-w-0">
                                      <div className="flex items-center gap-1">
                                        <span className="text-[10px] font-medium">
                                          {comment.user_name || "Unknown"}
                                        </span>
                                        <span className="text-[9px] text-muted-foreground/60">
                                          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
                                        </span>
                                      </div>
                                      <p className="text-[11px] text-muted-foreground whitespace-pre-line">
                                        {comment.body}
                                      </p>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* The task page on the FOR board; absent for someone who cannot open that board */}
                            {item.url && taskKey && (
                              <Link
                                to={`/tasks/${taskKey}`}
                                onClick={() => setOpen(false)}
                                className="inline-flex items-center gap-1 text-[11px] font-medium text-primary hover:underline"
                              >
                                <ExternalLink className="h-3 w-3" />
                                Open in Forge
                              </Link>
                            )}
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              )}

              <div className="pt-2 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full h-7 text-xs"
                  onClick={() => setTab("new")}
                >
                  <MessageSquarePlus className="h-3.5 w-3.5 mr-1" />
                  New Feedback
                </Button>
              </div>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
